import { useMemo } from 'react';
import { useSubroute } from './subroute.js';

/**
 * Which item of a list page is open, read from the address (owner, 21 October 2026: "editor
 * replaces list" on every routing page, each with a short URL).
 *
 * /manage/meridian/ivrs            the list
 * /manage/meridian/ivrs/main-menu  that menu's editor
 * /manage/meridian/ivrs/new        a new one
 *
 * The slug is the item's name, as Campaigns already does (memory: short URLs are human names,
 * never uuids). Two items with one name are told apart by -2, -3 in the order they appear. A
 * slug nobody has any more -- renamed, deleted, a stale bookmark -- shows the list rather than
 * an empty editor.
 */
export const slugOfName = (name: string): string =>
  name.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'item';

export function nameSlugs<T extends { id: string }>(items: ReadonlyArray<T>, nameOf: (t: T) => string): Map<string, string> {
  const out = new Map<string, string>();
  const taken = new Map<string, number>();
  for (const it of items) {
    let base = slugOfName(nameOf(it));
    if (base === 'new') base = 'new-1';
    const n = (taken.get(base) ?? 0) + 1;
    taken.set(base, n);
    out.set(it.id, n === 1 ? base : `${base}-${n}`);
  }
  return out;
}

export interface OpenItem<T> {
  /** The item whose editor is showing, if any. */
  item: T | null;
  /** True on /new. */
  creating: boolean;
  /** An item's slug, for links to it. */
  slugOf: (t: T) => string;
  open: (t: T) => void;
  openNew: () => void;
  /** Back to the list. */
  close: () => void;
  /** After a save: the editor of what was saved, by its (possibly new) name. */
  openByName: (name: string) => void;
}

export function useOpenItem<T extends { id: string }>(
  items: ReadonlyArray<T> | undefined,
  nameOf: (t: T) => string,
): OpenItem<T> {
  const route = useSubroute();
  const slugs = useMemo(() => nameSlugs(items ?? [], nameOf), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const head = route.rest[0];
  const creating = head === 'new';
  const item = !creating && head ? (items ?? []).find((t) => slugs.get(t.id) === head) ?? null : null;
  return {
    item,
    creating,
    slugOf: (t) => slugs.get(t.id) ?? slugOfName(nameOf(t)),
    open: (t) => route.go([slugs.get(t.id) ?? slugOfName(nameOf(t))]),
    openNew: () => route.go(['new']),
    close: () => route.go([]),
    openByName: (name) => route.go([slugOfName(name)], { replace: true }),
  };
}
